import React, { useEffect, useMemo, useState } from 'react';
import { translate } from '@shakkerai/util-i18n';
import { Icon } from '@shakkerai/components';
import { useAsyncEffect, useDebounceFn } from 'ahooks';
import { request } from '@shakkerai/services';
import { convert2CompressedUrl } from '@shakkerai/util-image';
import { useTempState, setTempState, getSharedData, useUnitData, getTempState } from '@starflow/app/store';
import { presetCheckpoints, BASETYPE_NAME_TO_VALUE, communityUrl, MODEL_FLUX } from '../../constants';
import { updatePartialState, usePartialState } from '../../store/state';
import { queryModelState } from '../../store/actions';
import { applyPresetModel } from '../../helpers';
import { Filter } from '../base/Filter';
import { ModelList } from '../base/List';
import styles from './index.module.scss';
import cx from 'classnames';

const sortOptions = [
  { value: '1', label: translate('model_recently_favorite') },
  { value: '2', label: translate('model_sort_option_most_used') },
];

const SPLICE_NUM = 6;

const PresetCheckpoints = React.memo(({ isFull, data }) => {
  const [expanded, setExpanded] = useState(false);
  const globaoMode = useTempState('globalMode');
  const renderedItems = isFull || expanded ? presetCheckpoints : presetCheckpoints.slice(0, SPLICE_NUM);

  const { presetBaseModelId: unitPresetBaseModelId } = useUnitData();
  const tempPickingModel = useTempState('tempPickingModel');
  const tempCheckpoint = useTempState('tempCheckpoint');
  const presetBaseModelId = tempPickingModel ? tempCheckpoint?.presetId : unitPresetBaseModelId;

  const hideCheckpointPreset = useTempState('hideCheckpointPreset');
  const [baseModelRunCount, setBaseModelRunCount] = useState();
  const handleSwitchToAllTab = (event) => {
    setTempState({ checkpointsTab: 'all' });
    event.stopPropagation();
  };

  const handleApplyPresetModel = (item) => {
    if (getTempState('tempPickingModel')) {
      setTempState('tempCheckpoint', { presetId: item.id });
      getTempState('tempCheckpointPickCallback')?.({ presetId: item.id });
    } else {
      applyPresetModel(item.id);
    }
  };

  const allowExpand = presetCheckpoints.length > 8;

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useAsyncEffect(async () => {
    const uuid = renderedItems[0].versionDetail.uuid;
    try {
      const result = await request(`/api/www/img/tool/style/model/baseDetail/${uuid}`);
      if (result.code === 0) {
        setBaseModelRunCount(result.data.runCount);
      }
    } catch (err) {
      console.log('api error', err);
    }
  }, []);

  const getContainerStyle = useMemo(() => {
    if (hideCheckpointPreset) {
      return {
        height: '0px',
        overflow: 'hidden',
        transition: 'all 0.3s ease',
        opacity: 0,
      };
    } else {
      return {
        height: data.items.length ? (renderedItems.length > SPLICE_NUM ? '346px' : '194px') : null,
        transition: 'all 0.3s ease',
        opacity: 1,
      };
    }
  }, [renderedItems, hideCheckpointPreset, data]);

  const majorHandler = (isFull, allowExpand, expanded) => {
    return (
      <div className="flex items-center justify-between text-sm text-[#2d2d2d]">
        <b>{translate('basemodel_preset')}</b>
        {!isFull && allowExpand && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center text-sm text-[#7f7f7f] font-normal"
          >
            <span>{translate(expanded ? 'collapse' : 'expand')}</span>
            <Icon
              className={`ml-1 shk-kuangjia-zhankai ${expanded ? 'rotate-180' : ''}`}
              fontSize={14}
              color="inherit"
            />
          </button>
        )}
      </div>
    );
  };
  return (
    <div className="px-5 pt-3" style={getContainerStyle}>
      {!isFull && majorHandler(isFull, allowExpand, expanded)}
      {(isFull ? renderedItems.slice(0, 1) : []).map((item) => {
        const using = presetBaseModelId === item.id;
        return (
          <div key={item.id} onClick={() => handleApplyPresetModel(item)} className={cx(styles['normal-gide-content'])}>
            <div className={cx('w-[748px] flex', styles['gid-content'], { [styles['gid-content-none']]: !using })}>
              <div className="w-[175px] h-[200px] bg-slate-600 relative rounded overflow-hidden">
                <img src={presetCheckpoints[0].imageVertical} className="w-full h-full" alt="" />
                {baseModelRunCount > 0 ? (
                  <div className="h-5 w-full flex justify-center items-center absolute bottom-[20px] text-[#fff] text-sm font-normal not-italic">
                    <Icon className="shk-danchuang-yunhangliang mr-1 relative top-[-1px]" fontSize={12} color="#fff" />
                    <span>
                      {baseModelRunCount >= 1000 ? `${(baseModelRunCount / 1000).toFixed(1)}k` : baseModelRunCount}
                    </span>
                  </div>
                ) : null}
              </div>
              <div className="flex-1 relative overflow-hidden">
                {using ? (
                  <span
                    className={cx(
                      'text-[#2C2C2C] text-xs not-italic font-medium flex justify-center items-center pl-5 pr-4 h-[26px] absolute right-0 top-0',
                    )}
                  >
                    <div className="absolute top-0 -right-2 bg-primary w-20 h-[26px] rounded-bl-lg skew-x-12" />
                    <span className="relative z-[2]">{translate('preset_basemodle_using')}</span>
                  </span>
                ) : null}
                <div className="h-[70px]  flex items-center px-4">
                  <span className={cx('block', styles['gide-icon'])}>{translate('preset_basemodle_update')}</span>
                  <h5 className="text-[#2D2D2D] text-sm font-medium not-italic">
                    {translate('preset_basemodle_shakker')}
                  </h5>
                </div>
                <ul className="relative top-[-6px]">
                  <li className={styles['gide-single-line']}>
                    <i />
                    {translate('preset_basemodle_desc1')}
                  </li>
                  <li className={styles['gide-single-line']}>
                    <i />
                    {translate('preset_basemodle_desc2')}
                  </li>
                  <li className={styles['gide-single-line']}>
                    <i />
                    {translate('preset_basemodle_desc3')}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        );
      })}
      {isFull && <div className="mt-3">{majorHandler(isFull, allowExpand, expanded)}</div>}
      <div
        className={`mt-3 ${
          isFull
            ? 'grid grid-cols-5 gap-4'
            : expanded && allowExpand
              ? 'flex flex-wrap justify-start'
              : 'flex flex-nowrap justify-start'
        } `}
      >
        {(isFull ? renderedItems.slice(1, renderedItems.length) : renderedItems).map((item, idx) => {
          const using = presetBaseModelId === item.id;
          const itemBaseType = BASETYPE_NAME_TO_VALUE[item.baseType];
          if (!isFull && idx === 0) {
            return (
              <div
                key={item.id}
                onClick={() => handleApplyPresetModel(item)}
                style={{ border: using ? '1px solid var(--color-primary)' : null }}
                className={`relative group bg-[#e4e6e8] overflow-hidden mb-[12px] mr-[12px] rounded cursor-pointer w-[342px] h-[142px]`}
              >
                {using ? (
                  <span
                    className={cx(
                      'text-[#2C2C2C] text-xs not-italic font-medium flex justify-center items-center pl-5 pr-4 h-[26px] absolute right-0 top-0 z-10',
                    )}
                  >
                    <div className="absolute top-0 left-0 bg-primary w-20 h-[26px] rounded-bl-lg skew-x-12" />
                    <span className="relative z-[2]">{translate('style_using')}</span>
                  </span>
                ) : null}
                {itemBaseType === MODEL_FLUX && globaoMode === 'pro' && (
                  <span className="absolute z-[2] top-2 left-2 px-2 bg-black/60 rounded-[2px] text-white leading-[20px] text-xs">
                    {translate('model_flux_short')}
                  </span>
                )}
                <img
                  src={convert2CompressedUrl(presetCheckpoints[0].imageBanner, 470)}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition duration-500"
                />
                <div className="h-full w-full relative">
                  <div className="absolute flex justify-between w-full h-5 px-3 bottom-[11px]">
                    <div className="flex items-center">
                      <span className={cx('grow-0', styles['gide-icon'])}>{translate('preset_basemodle_update')}</span>
                      <span className="text-white text-xs font-medium">{item.displayName}</span>
                    </div>
                    {baseModelRunCount > 0 ? (
                      <div className="flex items-center drop-shadow-sm">
                        <Icon className="shk-danchuang-yunhangliang" fontSize={12} color="#fff" />
                        <span className="text-white text-xs font-medium ml-1">
                          {baseModelRunCount >= 1000 ? `${(baseModelRunCount / 1000).toFixed(1)}k` : baseModelRunCount}
                        </span>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            );
          }
          return (
            <div
              key={item.id}
              onClick={() => handleApplyPresetModel(item)}
              className={`relative group bg-[#e4e6e8] overflow-hidden ${
                idx === SPLICE_NUM - 1 ? 'mr-0' : 'mr-[12px]'
              } rounded cursor-pointer ${isFull ? 'w-[175px] h-[235px]' : 'w-[106px] h-[142px]'}`}
            >
              {itemBaseType === MODEL_FLUX && globaoMode === 'pro' && (
                <span className="absolute z-[2] top-2 left-2 px-2 bg-black/60 rounded-[2px] text-white leading-[20px] text-xs">
                  {translate('model_flux_short')}
                </span>
              )}
              <img
                src={convert2CompressedUrl(item.imageUrl, 470)}
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition duration-500"
              />
              <div className="absolute w-full z-2 flex flex-col items-center justify-end pb-3 bg-gradient-to-b from-black/0 to-black/80 h-[72px] bottom-0">
                <span className="text-white text-xs font-semibold text-center">{item.displayName}</span>
                {using && <span className="mt-1 text-xs text-white/60">{translate('style_using')}</span>}
              </div>
              {using && <div className="absolute inset-0 z-3 border-2 border-primary rounded" />}
            </div>
          );
        })}
        {allowExpand && isFull ? (
          <div
            onClick={handleSwitchToAllTab}
            className={`relative group bg-[#e4e6e8] overflow-hidden rounded cursor-pointer ${
              isFull ? 'w-[175px] h-[235px]' : 'w-[106px] h-[142px]'
            }`}
          >
            <img
              src="https://models-online-persist.shakker.ai/img/94c944b4511046f3985a237724cb7ce8/70287fd3701122fb4a584df8fdafc8167e7983e41576a2519235c2e76f5693e6.png"
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition duration-500"
            />
            <div className="absolute inset-0 z-2 flex flex-col items-center justify-center bg-gradient-to-b from-black/0 to-black/80">
              <Icon className="shk-danchuang-gengduomoxing1" fontSize={20} color="#fff" />
              {isFull && <span className="mt-2 text-sm text-white font-normal">{translate('basemodel_all')}</span>}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
});

const FavoritedModels = React.memo(({ data, filters }) => {
  const handleApplyFilter = (value) => {
    updatePartialState('checkpoint.favorites.filters', value);

    if (value.sortType) {
      updatePartialState('checkpoint.favorites.data', { items: [], hasMore: true });
      queryModelState('checkpoint', 'favorites', {}, false);
    }
  };

  console.log(`[DEBUG] data`, { data, filters });

  const listData = useMemo(() => {
    const items = data.items
      .filter((item) => {
        if (filters.category !== '0' && item.category !== filters.category) {
          return false;
        }

        if (filters.keyword) {
          const keyword = filters.keyword.toLowerCase();
          const targetString = `${item.name || ''}${item.userName || ''}`.toLowerCase();

          if (!targetString.includes(keyword)) {
            return false;
          }
        }

        if (filters.baseTypes?.length && !filters.baseTypes.includes(String(BASETYPE_NAME_TO_VALUE[item.baseType]))) {
          return false;
        }

        return true;
      })
      .filter((item, index, array) => array.findIndex((t) => t?.versionId === item?.versionId) === index);

    return { items, total: items.length, loading: data.loading, hasMore: data.hasMore };
  }, [data, filters]);

  const handleCheckpointScrollChange = (event) => {
    // 一行就不要去折叠了
    if (data.items.length > 5) {
      const { scrollTop } = event.target;
      // const loraHeight = loraContainerRef?.current?.clientHeight || 28;
      if (scrollTop > 10) {
        setTempState({ hideCheckpointPreset: true });
      } else {
        setTempState({ hideCheckpointPreset: false });
      }
    }
  };
  const promptScrollThrottle = useDebounceFn(handleCheckpointScrollChange, { wait: 20 });
  const handleCheckpointScroll = (event) => {
    promptScrollThrottle.run(event);
  };

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      <div
        className="relative z-[2] flex items-center justify-between px-5 py-2"
        style={{ boxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.05)' }}
      >
        <b className="text-sm text-[#2d2d2d] translate-y-0.5">{translate('common_fav')}</b>
        <Filter value={filters} sortOptions={sortOptions} onSubmit={handleApplyFilter} />
      </div>
      <div className="flex-1 overflow-auto px-5 tiny-scrollbar bg-[#f9fbfb]" onScroll={handleCheckpointScroll}>
        <ModelList data={listData} showTag={!filters.keyword} from="favorite" />
      </div>
    </div>
  );
});

export const Favorites = React.memo(() => {
  const { filters, data } = usePartialState('checkpoint.favorites');

  const isEmpty = useMemo(() => {
    if (data.loading) return false;
    if (data.items.length) return false;
    if (filters.keyword) return false;
    if (filters.baseType !== '0') return false;

    return true;
  }, [data.items.length, data.loading, filters]);

  useEffect(() => {
    updatePartialState('checkpoint.favorites.filters', { page: 1 });
    updatePartialState('checkpoint.favorites.data', { items: [], hasMore: true });
    queryModelState('checkpoint', 'favorites');
  }, []);
  if (data.loading) {
    return (
      <div className="flex items-center justify-center mt-32 text-xs text-[#7f7f7f]">{translate('loading')}...</div>
    );
  }
  return (
    <main className="flex flex-col items-stretch flex-1 overflow-auto tiny-scrollbar">
      <PresetCheckpoints isFull={isEmpty} data={data} />
      {isEmpty ? (
        <div className="flex items-center justify-center mt-5 px-4 text-center text-sm text-[#7f7f7f] mb-[27px]">
          <span>{translate('basemodel_toshakker1')}</span>
          <a href={communityUrl} target="_blank" className="flex items-center mx-1 text-primary hover:underline">
            <Icon className="shk-danchuang-moxingguangchang text-primary" fontSize={14} />
            {translate('basemodel_toshakker3')}
          </a>
          <span>{translate('basemodel_toshakker2')}</span>
        </div>
      ) : (
        <FavoritedModels data={data} filters={filters} />
      )}
    </main>
  );
});
